import { useEffect, useRef, useState, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query';
import { fetchTicket, fetchTicketActions, fetchTicketTimeline } from '@/lib/api';
import { useNexusStore } from '@/stores/nexusStore';
import { StatusDot } from '@/components/shared/StatusDot';
import { ScrollArea } from '@/components/ui/scroll-area';
import { ArrowLeft, Clock, Shield, Zap, User, Target, FileText, Activity, GitCommit, CheckCircle, XCircle, Bell, UserCog, ChevronUp, MessageSquare } from 'lucide-react';
import socket from '@/lib/socket';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const PRIORITY_STYLES = { P1: 'badge-p1', P2: 'badge-p2', P3: 'badge-p3', P4: 'badge-p4', P5: 'badge-p5' };
const STATUS_MAP = {
  NEW: { label: 'New', dot: 'info' }, TRIAGING: { label: 'Triaging', dot: 'processing' },
  IN_PROGRESS: { label: 'In Progress', dot: 'active' }, AGENT_RESOLVING: { label: 'Agent Resolving', dot: 'processing' },
  PENDING_APPROVAL: { label: 'Pending Approval', dot: 'warning' }, RESOLVED: { label: 'Resolved', dot: 'success' },
  CLOSED: { label: 'Closed', dot: 'idle' },
};

const formatTime = (ts) => { try { return new Date(ts).toLocaleTimeString('en-US', { hour12: false, hour: '2-digit', minute: '2-digit', second: '2-digit' }); } catch { return '--:--:--'; } };

// Circular confidence gauge
const ConfidenceGauge = ({ value = 0 }) => {
  const radius = 36;
  const circumference = 2 * Math.PI * radius;
  const progress = circumference - (value / 100) * circumference;
  const color = value >= 80 ? 'var(--nexus-success)' : value >= 60 ? 'var(--nexus-warning)' : 'var(--nexus-danger)';
  return (
    <div className="relative flex items-center justify-center w-24 h-24" data-testid="confidence-gauge">
      <svg className="w-24 h-24 -rotate-90" viewBox="0 0 80 80">
        <circle cx="40" cy="40" r={radius} fill="none" stroke="var(--nexus-border)" strokeWidth="4" />
        <circle cx="40" cy="40" r={radius} fill="none" stroke={color} strokeWidth="4" strokeLinecap="round"
          strokeDasharray={circumference} strokeDashoffset={progress}
          style={{ transition: 'stroke-dashoffset 1s ease-out' }} />
      </svg>
      <div className="absolute flex flex-col items-center">
        <span className="font-heading text-xl font-bold tabular-nums" style={{ color }}>{value.toFixed(0)}</span>
        <span className="text-[8px] text-[var(--nexus-text-muted)] uppercase tracking-wider">confidence</span>
      </div>
    </div>
  );
};

// Live SLA countdown
const SlaBlock = ({ deadline }) => {
  const [remaining, setRemaining] = useState('');
  const [urgency, setUrgency] = useState('green');
  useEffect(() => {
    if (!deadline) return;
    const update = () => {
      const diffMs = new Date(deadline) - new Date();
      if (diffMs <= 0) { setRemaining('BREACHED'); setUrgency('red'); return; }
      const h = Math.floor(diffMs / 3600000); const m = Math.floor((diffMs % 3600000) / 60000); const s = Math.floor((diffMs % 60000) / 1000);
      setRemaining(h > 0 ? `${h}h ${m}m ${s}s` : m > 0 ? `${m}m ${s}s` : `${s}s`);
      setUrgency(diffMs < 3600000 ? 'red' : diffMs < 14400000 ? 'orange' : 'green');
    };
    update(); const timer = setInterval(update, 1000); return () => clearInterval(timer);
  }, [deadline]);
  const colors = { red: 'text-[var(--nexus-danger)] bg-red-500/10 border-red-500/20', orange: 'text-[var(--nexus-warning)] bg-amber-500/10 border-amber-500/20', green: 'text-[var(--nexus-success)] bg-green-500/10 border-green-500/20' };
  return (
    <div className={`p-2.5 rounded-lg border ${colors[urgency]}`} data-testid="sla-block">
      <div className="flex items-center gap-1.5 mb-0.5"><Clock size={11} /><span className="text-[10px] uppercase tracking-wider font-medium">SLA Remaining</span></div>
      <span className="font-mono text-lg font-bold tabular-nums">{remaining}</span>
      {urgency === 'red' && <p className="text-[9px] mt-0.5 opacity-70">SLA breach imminent</p>}
    </div>
  );
};

export default function IncidentDetailPage() {
  const { id } = useParams();
  const activityFeed = useNexusStore((s) => s.activityFeed);
  const feedRef = useRef(null);
  const [ticketActions, setTicketActions] = useState([]);
  const [isAriaThinking, setIsAriaThinking] = useState(false);
  const [userScrolled, setUserScrolled] = useState(false);
  const queryClient = useQueryClient();
  const token = localStorage.getItem('nexus_token');

  const { data: ticket, isLoading: loadingTicket } = useQuery({ queryKey: ['ticket', id], queryFn: () => fetchTicket(id), refetchInterval: 5000 });
  const { data: actions } = useQuery({ queryKey: ['ticket-actions', id], queryFn: () => fetchTicketActions(id), refetchInterval: 8000 });
  const { data: timeline } = useQuery({ queryKey: ['ticket-timeline', id], queryFn: () => fetchTicketTimeline(id) });

  // Socket subscription
  useEffect(() => {
    socket.emit('subscribe_ticket', id);
    const handler = (data) => {
      if (data.ticket_id === id) {
        setTicketActions(prev => [data, ...prev].slice(0, 100));
        if (data.status === 'running') setIsAriaThinking(true); else setIsAriaThinking(false);
        if (['validate', 'pir', 'escalate', 'human_approval', 'human_override'].includes(data.node)) queryClient.invalidateQueries(['ticket', id]);
      }
    };
    socket.on('agent:action', handler);
    return () => { socket.off('agent:action', handler); };
  }, [id, queryClient]);

  // Auto-scroll with pause on manual scroll
  const scrollViewportRef = useRef(null);
  useEffect(() => {
    if (!userScrolled && feedRef.current) {
      const vp = feedRef.current.querySelector('[data-radix-scroll-area-viewport]');
      if (vp) vp.scrollTop = 0;
    }
  }, [ticketActions.length, userScrolled]);

  // Deduped actions
  const seen = new Set();
  const allActions = [...ticketActions, ...(activityFeed.filter(e => e.ticket_id === id)), ...(actions || [])].filter(a => { if (seen.has(a.id)) return false; seen.add(a.id); return true; }).slice(0, 60);

  // Approve/Reject mutation
  const approveMutation = useMutation({
    mutationFn: async ({ stepIndex, approved }) => {
      const { data } = await axios.post(`${BACKEND_URL}/api/tickets/${id}/approve-step`, { step_index: stepIndex, approved }, { headers: { Authorization: `Bearer ${token}` } });
      return data;
    },
    onSuccess: (data) => { toast.success(data.status === 'approved' ? 'Step approved' : 'Step rejected'); queryClient.invalidateQueries(['ticket', id]); },
    onError: () => toast.error('Failed to process approval'),
  });

  // Override mutation
  const overrideMutation = useMutation({
    mutationFn: async (body) => {
      const { data } = await axios.post(`${BACKEND_URL}/api/tickets/${id}/override`, body, { headers: { Authorization: `Bearer ${token}` } });
      return data;
    },
    onSuccess: () => { toast.success('Override applied'); queryClient.invalidateQueries(['ticket', id]); },
    onError: () => toast.error('Override failed'),
  });

  if (loadingTicket) return (
    <div className="w-full max-w-[1600px] mx-auto space-y-4">
      <div className="h-8 w-48 bg-[var(--nexus-bg-elevated)] rounded animate-pulse" />
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-4"><div className="lg:col-span-3 h-96 bg-[var(--nexus-bg-elevated)] rounded-xl animate-pulse" /><div className="lg:col-span-2 h-96 bg-[var(--nexus-bg-elevated)] rounded-xl animate-pulse" /></div>
    </div>
  );
  if (!ticket || ticket.error) return <div className="flex flex-col items-center justify-center h-full gap-4"><p className="text-[var(--nexus-text-muted)]">Ticket not found</p><Link to="/incidents" className="text-xs text-[var(--nexus-accent)] hover:underline">Back to incidents</Link></div>;

  const sm = STATUS_MAP[ticket.status] || { label: ticket.status, dot: 'info' };

  return (
    <div data-testid="incident-detail-page" className="w-full max-w-[1600px] mx-auto">
      <Link to="/incidents" className="flex items-center gap-1 text-xs text-[var(--nexus-text-muted)] hover:text-[var(--nexus-text-secondary)] transition-colors mb-3" data-testid="back-to-incidents"><ArrowLeft size={14} /> Back to Incidents</Link>

      {/* Ticket header */}
      <div className="flex items-start justify-between mb-4">
        <div>
          <div className="flex items-center gap-3">
            <h1 className="font-heading text-xl font-semibold text-[var(--nexus-text-primary)]">{ticket.id}</h1>
            <span className={`font-mono text-[10px] px-1.5 py-0.5 rounded ${PRIORITY_STYLES[ticket.priority]}`}>{ticket.priority}</span>
            <div className="flex items-center gap-1.5"><StatusDot status={sm.dot} pulse={sm.dot === 'processing'} size="md" /><span className="text-xs text-[var(--nexus-text-secondary)]" data-testid="ticket-status">{sm.label}</span></div>
          </div>
          <p className="text-sm text-[var(--nexus-text-secondary)] mt-1">{ticket.title}</p>
        </div>
        <ConfidenceGauge value={ticket.confidence || 0} />
      </div>

      {/* Split layout: 60% feed | 40% info */}
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
        {/* Left: ARIA Agent Feed (60% = 3/5) */}
        <div className="lg:col-span-3">
          <div className="nexus-card relative overflow-hidden">
            <div className="p-3 border-b border-[var(--nexus-border)]">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2"><Activity size={14} className="text-[var(--nexus-accent)]" /><h2 className="text-sm font-semibold text-[var(--nexus-text-primary)]">ARIA — Live Activity Feed</h2></div>
                <div className="flex items-center gap-1.5">
                  {isAriaThinking && (<span className="flex items-center gap-1.5 mr-2"><span className="flex gap-0.5"><span className="w-1 h-1 rounded-full bg-[var(--nexus-accent)] animate-bounce" style={{animationDelay:'0ms'}} /><span className="w-1 h-1 rounded-full bg-[var(--nexus-accent)] animate-bounce" style={{animationDelay:'150ms'}} /><span className="w-1 h-1 rounded-full bg-[var(--nexus-accent)] animate-bounce" style={{animationDelay:'300ms'}} /></span><span className="font-mono text-[10px] text-[var(--nexus-accent)]">ARIA is analyzing...</span></span>)}
                  <StatusDot status="live" pulse size="sm" /><span className="font-mono text-[10px] text-[var(--nexus-accent)]">LIVE</span>
                </div>
              </div>
            </div>
            <ScrollArea className="h-[500px]" ref={feedRef}>
              <div className="p-2 space-y-1" data-testid="agent-action-log">
                {allActions.map((action, idx) => (
                  <div key={action.id || idx} data-testid="feed-entry" className={`p-2.5 rounded-lg border ${
                    action.type === 'APPROVAL_NEEDED' ? 'bg-purple-500/5 border-purple-500/20' :
                    action.type === 'ERROR' || action.severity === 'critical' ? 'bg-red-500/5 border-red-500/10' :
                    action.type === 'SUCCESS' || action.severity === 'success' ? 'bg-green-500/5 border-green-500/10' :
                    'bg-[var(--nexus-bg-base)] border-[var(--nexus-border)]'
                  } ${idx === 0 && ticketActions.length > 0 ? 'animate-nexus-slide-in' : ''}`}>
                    <div className="flex items-start gap-2">
                      <div className="mt-0.5">
                        {action.type === 'APPROVAL_NEEDED' ? <Bell size={12} className="text-purple-400" /> :
                         action.type === 'SUCCESS' ? <CheckCircle size={12} className="text-[var(--nexus-success)]" /> :
                         action.type === 'ERROR' ? <XCircle size={12} className="text-[var(--nexus-danger)]" /> :
                         <StatusDot status={action.severity || 'info'} size="sm" />}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-0.5">
                          <span className="font-mono text-[10px] font-semibold text-[var(--nexus-accent)]">{action.agent_name}</span>
                          {action.node && <span className="text-[10px] px-1 py-0 rounded bg-[var(--nexus-bg-elevated)] text-[var(--nexus-text-muted)] border border-[var(--nexus-border)]">{action.node}</span>}
                          <span className="font-mono text-[10px] text-[var(--nexus-text-muted)]">{formatTime(action.timestamp)}</span>
                        </div>
                        <p className="text-[11px] text-[var(--nexus-text-secondary)] font-mono leading-relaxed">{action.action}</p>
                        {action.type === 'APPROVAL_NEEDED' && (
                          <div className="flex items-center gap-2 mt-2">
                            <button onClick={() => approveMutation.mutate({ stepIndex: action.data?.step_index || 0, approved: true })}
                              className="px-3 py-1 rounded-md bg-[var(--nexus-success)] bg-opacity-15 border border-green-500/30 text-green-300 text-[10px] font-medium hover:bg-opacity-25 transition-colors" data-testid="approve-step">Approve</button>
                            <button onClick={() => approveMutation.mutate({ stepIndex: action.data?.step_index || 0, approved: false })}
                              className="px-3 py-1 rounded-md bg-[var(--nexus-danger)] bg-opacity-15 border border-red-500/30 text-red-300 text-[10px] font-medium hover:bg-opacity-25 transition-colors" data-testid="reject-step">Reject</button>
                          </div>
                        )}
                      </div>
                      <div className="flex flex-col items-end gap-0.5 flex-shrink-0">
                        {action.confidence > 0 && <span className="font-mono text-[10px] text-[var(--nexus-text-muted)] tabular-nums">{action.confidence?.toFixed(0)}%</span>}
                        {action.duration_ms > 0 && <span className="font-mono text-[10px] text-[var(--nexus-text-muted)] tabular-nums">{action.duration_ms < 1000 ? `${action.duration_ms}ms` : `${(action.duration_ms / 1000).toFixed(1)}s`}</span>}
                      </div>
                    </div>
                  </div>
                ))}
                {allActions.length === 0 && <div className="text-center py-8 text-[var(--nexus-text-muted)] text-xs">No agent actions recorded for this ticket</div>}
              </div>
            </ScrollArea>
          </div>
        </div>

        {/* Right: Ticket details (40% = 2/5) */}
        <div className="lg:col-span-2 space-y-3">
          {/* SLA Block */}
          {ticket.status !== 'RESOLVED' && ticket.status !== 'CLOSED' && ticket.sla_deadline && (
            <SlaBlock deadline={ticket.sla_deadline} />
          )}

          {/* Metadata */}
          <div className="nexus-card relative overflow-hidden p-4">
            <h3 className="text-sm font-semibold text-[var(--nexus-text-primary)] mb-3">Ticket Details</h3>
            <div className="space-y-2.5">
              <DetailRow icon={FileText} label="Type" value={ticket.type} />
              <DetailRow icon={Target} label="Assigned Agent" value={ticket.assigned_agent} accent />
              <DetailRow icon={User} label="Assigned Human" value={ticket.assigned_human || 'Unassigned'} />
              <DetailRow icon={Clock} label="SLA Deadline" value={ticket.sla_deadline ? new Date(ticket.sla_deadline).toLocaleString() : 'N/A'} />
              <DetailRow icon={Clock} label="Created" value={ticket.created_at ? new Date(ticket.created_at).toLocaleString() : '-'} />
              {ticket.resolved_at && <DetailRow icon={Shield} label="Resolved At" value={new Date(ticket.resolved_at).toLocaleString()} />}
            </div>
          </div>

          {/* Root Cause */}
          {ticket.root_cause && (
            <div className="nexus-card relative overflow-hidden p-4" data-testid="pir-section">
              <h3 className="text-sm font-semibold text-[var(--nexus-text-primary)] mb-2 flex items-center gap-2"><Zap size={14} className="text-[var(--nexus-warning)]" /> Root Cause</h3>
              <p className="text-xs text-[var(--nexus-text-secondary)] font-mono leading-relaxed bg-[var(--nexus-bg-base)] rounded-lg p-3 border border-[var(--nexus-border)]">{ticket.root_cause}</p>
            </div>
          )}

          {/* Resolution */}
          {ticket.resolution && (
            <div className="nexus-card relative overflow-hidden p-4">
              <h3 className="text-sm font-semibold text-[var(--nexus-text-primary)] mb-2 flex items-center gap-2"><Shield size={14} className="text-[var(--nexus-success)]" /> Resolution</h3>
              <p className="text-xs text-[var(--nexus-text-secondary)] leading-relaxed">{ticket.resolution}</p>
            </div>
          )}

          {/* Affected CIs */}
          {ticket.affected_cis?.length > 0 && (
            <div className="nexus-card relative overflow-hidden p-4">
              <h3 className="text-sm font-semibold text-[var(--nexus-text-primary)] mb-2">Affected CIs</h3>
              <div className="flex flex-wrap gap-1.5">
                {ticket.affected_cis.map((ci, i) => (
                  <span key={i} className="font-mono text-[10px] px-2 py-1 rounded-md bg-[var(--nexus-bg-base)] border border-[var(--nexus-border)] text-[var(--nexus-accent)]">{ci}</span>
                ))}
              </div>
            </div>
          )}

          {/* Human Override Panel */}
          <div className="nexus-card relative overflow-hidden p-4" data-testid="human-override-panel">
            <h3 className="text-sm font-semibold text-[var(--nexus-text-muted)] mb-3 flex items-center gap-2"><UserCog size={14} /> Human Override</h3>
            <div className="grid grid-cols-2 gap-2">
              <button onClick={() => overrideMutation.mutate({ action: 'reassign', agent: 'ARIA' })} data-testid="override-reassign"
                className="px-2 py-1.5 rounded-lg bg-[var(--nexus-bg-base)] border border-[var(--nexus-border)] text-[10px] text-[var(--nexus-text-secondary)] hover:bg-white/[0.04] transition-colors">Reassign</button>
              <button onClick={() => overrideMutation.mutate({ action: 'escalate', reason: 'Manual escalation' })} data-testid="override-escalate"
                className="px-2 py-1.5 rounded-lg bg-[var(--nexus-bg-base)] border border-[var(--nexus-border)] text-[10px] text-[var(--nexus-text-secondary)] hover:bg-white/[0.04] transition-colors">Escalate</button>
              <button onClick={() => overrideMutation.mutate({ action: 'close' })} data-testid="override-close"
                className="px-2 py-1.5 rounded-lg bg-[var(--nexus-bg-base)] border border-[var(--nexus-border)] text-[10px] text-[var(--nexus-text-secondary)] hover:bg-white/[0.04] transition-colors">Close</button>
              <button onClick={() => { const note = prompt('Add note:'); if (note) overrideMutation.mutate({ action: 'add_note', note }); }} data-testid="override-add-note"
                className="px-2 py-1.5 rounded-lg bg-[var(--nexus-bg-base)] border border-[var(--nexus-border)] text-[10px] text-[var(--nexus-text-secondary)] hover:bg-white/[0.04] transition-colors flex items-center justify-center gap-1"><MessageSquare size={10} /> Note</button>
            </div>
          </div>

          {/* Timeline */}
          {timeline?.length > 0 && (
            <div className="nexus-card relative overflow-hidden p-4">
              <h3 className="text-sm font-semibold text-[var(--nexus-text-primary)] mb-3 flex items-center gap-2"><GitCommit size={14} className="text-[var(--nexus-accent)]" /> Event Timeline</h3>
              <div className="space-y-0">
                {timeline.map((event, idx) => (
                  <div key={idx} className="flex gap-3 relative">
                    <div className="flex flex-col items-center">
                      <div className={`w-2 h-2 rounded-full mt-1.5 ${event.type === 'resolved' ? 'bg-[var(--nexus-success)]' : event.type === 'rca' ? 'bg-[var(--nexus-warning)]' : 'bg-[var(--nexus-accent)]'}`} />
                      {idx < timeline.length - 1 && <div className="w-px flex-1 bg-[var(--nexus-border)]" />}
                    </div>
                    <div className="pb-3 flex-1">
                      <div className="flex items-center gap-2"><span className="text-[11px] font-medium text-[var(--nexus-text-primary)]">{event.label}</span><span className="font-mono text-[10px] text-[var(--nexus-accent)]">{event.agent}</span></div>
                      <p className="text-[10px] text-[var(--nexus-text-muted)] mt-0.5">{event.detail}</p>
                      <span className="font-mono text-[9px] text-[var(--nexus-text-muted)]">{event.time ? new Date(event.time).toLocaleString() : ''}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Description */}
          <div className="nexus-card relative overflow-hidden p-4">
            <h3 className="text-sm font-semibold text-[var(--nexus-text-primary)] mb-2">Description</h3>
            <p className="text-xs text-[var(--nexus-text-secondary)] leading-relaxed">{ticket.description}</p>
          </div>
        </div>
      </div>
    </div>
  );
}

const DetailRow = ({ icon: Icon, label, value, accent }) => (
  <div className="flex items-center justify-between">
    <div className="flex items-center gap-2"><Icon size={12} className="text-[var(--nexus-text-muted)]" /><span className="text-[11px] text-[var(--nexus-text-muted)]">{label}</span></div>
    <span className={`text-[11px] font-mono ${accent ? 'text-[var(--nexus-accent)]' : 'text-[var(--nexus-text-secondary)]'}`}>{value}</span>
  </div>
);
